'use client'

import React, { useState } from 'react'
import { Star, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import type { CSATSurveyProps } from '@/lib/csat/types'

export function CSATSurvey({
  question,
  onRatingClick,
  onFeedbackSubmit,
  onDismiss,
  feedbackBehavior,
  translateFollowUpQuestion,
}: CSATSurveyProps) {
  const [rating, setRating] = useState<number | null>(null)
  const [previewRating, setPreviewRating] = useState<number | null>(null)
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)

  const t = useTranslations('csat')

  const handleDismiss = () => {
    onDismiss({ rating, feedback })
  }

  const renderSurveyBody = () => {
    // If the user has not voted yet, show the rating options
    if (rating === null) {
      return (
        <>
          <div className="mb-4 flex items-start gap-2">
            <h5 className="flex-1 text-lg font-semibold text-gray-900">{question}</h5>
            <button
              onClick={handleDismiss}
              className="ml-auto rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-6 flex justify-center" onMouseLeave={() => setPreviewRating(null)}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                onClick={() => {
                  setRating(value)
                  onRatingClick({ rating: value, feedback })
                }}
                onMouseEnter={() => setPreviewRating(value)}
                className="p-2 text-gray-300 transition-colors hover:text-yellow-400"
                type="button"
              >
                <Star
                  className="h-8 w-8 cursor-pointer"
                  fill={value <= (previewRating ?? rating ?? 0) ? 'currentColor' : 'none'}
                />
              </button>
            ))}
          </div>
        </>
      )
    }

    const shouldShowFeedback = () => {
      if (rating === null || isSubmittingFeedback) return false

      const behavior = feedbackBehavior?.showWhen ?? 'low_ratings'

      switch (behavior) {
        case 'always':
          return true
        case 'low_ratings':
          return rating <= 3
        case 'high_ratings':
          return rating > 3
        case 'high_and_low_ratings':
          return rating <= 3 || rating > 3 // Show for all ratings
        case 'never':
          return false
        default:
          return rating <= 3
      }
    }

    if (shouldShowFeedback()) {
      return (
        <>
          <div className="mb-4 flex items-start gap-2">
            <div className="flex flex-col gap-1">
              <h5 className="flex-1 text-lg font-semibold text-gray-900">{t('send_us_feedback_title')}</h5>
              <p className="text-sm text-gray-500">
                {(() => {
                  if (!translateFollowUpQuestion) {
                    return t('send_us_feedback_description')
                  }

                  if (feedbackBehavior?.showWhen === 'high_and_low_ratings') {
                    const ratingType = rating !== null && rating > 3 ? 'high' : 'low'
                    return translateFollowUpQuestion(ratingType)
                  }

                  return translateFollowUpQuestion()
                })()}
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="ml-auto rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-4 px-2">
            <textarea
              value={feedback ?? ''}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('textarea.feedback_placeholder')}
              disabled={isSubmittingFeedback}
              className="focus:ring-energy-blue min-h-20 w-full resize-y rounded-lg border border-gray-300 p-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none disabled:bg-gray-100"
            />
            <Button
              onClick={() => {
                setIsSubmittingFeedback(true)
                onFeedbackSubmit({ rating, feedback: feedback?.trim() ?? '' })
              }}
              disabled={isSubmittingFeedback}
              size="sm"
              variant="default"
              className="bg-energy-blue"
              type="button"
            >
              {t('button.send_feedback')}
            </Button>
          </div>
        </>
      )
    }

    return (
      <div className="flex justify-center px-4 py-4">
        <p className="text-base font-medium text-gray-900">{t('desc.feedback_sent_success')}</p>
      </div>
    )
  }

  return <div className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-lg">{renderSurveyBody()}</div>
}
